import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useReducedMotion } from "framer-motion";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import AdminDialog from "@/components/admin/AdminDialog";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import NewSinceLoginPill from "@/components/admin/NewSinceLoginPill";
import { getLeadTypeLabel } from "@/components/admin/LeadDetailsContent";
import useAttentionSeen from "@/hooks/useAttentionSeen";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { formatDateMdY, formatDateTimeMdY } from "@/utils/dateFormat";
import { visitorLeadSeries } from "@/utils/insightSeries";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const MONTH_PAGE_SIZES = [5, 10, 15, 20];
const DEFAULT_MONTH_PAGE_SIZE = 10;

function monthPageWindow(total, page, pageSize) {
  const size = MONTH_PAGE_SIZES.includes(pageSize) ? pageSize : DEFAULT_MONTH_PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil((total || 0) / size));
  const safePage = Math.min(Math.max(1, page || 1), totalPages);
  const start = (safePage - 1) * size;
  return { size, totalPages, safePage, start, end: start + size };
}

function monthPayload(payload) {
  if (payload?.key) return payload;
  if (payload?.payload?.key) return payload.payload;
  return null;
}

function SoldMonthDot({
  cx,
  cy,
  payload,
  selectedKey,
  panelOpen,
  onSelect,
  hollow,
  color = "#3b82f6",
  unit = "sold",
  listLabel = "cars",
  testPrefix = "sold-month-dot",
  persistent = false,
  interactive = true,
  expandable = true,
}) {
  const month = monthPayload(payload);
  if (!Number.isFinite(cx) || !Number.isFinite(cy) || !month?.key) return null;
  // Hover draws a second dot on top of the real one. That copy must take the click too.
  const clickable = Boolean(expandable && interactive);
  const tabbable = clickable && persistent;
  const selected = Boolean(clickable && panelOpen && month.key === selectedKey);
  const count = Number(month.count) || 0;
  const label = `${month.label || "Month"}, ${count} ${unit}`;
  return (
    <g
      role={tabbable ? "button" : undefined}
      tabIndex={tabbable ? 0 : undefined}
      className={clickable ? "sold-month-dot" : "sold-month-dot sold-month-dot-static"}
      aria-label={tabbable ? (selected ? `${label}. Hide the ${listLabel}.` : `${label}. Show the ${listLabel}.`) : undefined}
      aria-pressed={tabbable ? selected : undefined}
      data-testid={persistent ? `${testPrefix}-${month.key}` : undefined}
      style={{ cursor: clickable ? "pointer" : "default" }}
      onClick={clickable ? (event) => {
        event.stopPropagation();
        onSelect(month);
      } : undefined}
      onKeyDown={tabbable ? (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        event.stopPropagation();
        onSelect(month);
      } : undefined}
    >
      <circle className="sold-month-dot-hit" cx={cx} cy={cy} r={12} fill="transparent" pointerEvents="all" />
      <circle
        className="sold-month-dot-mark"
        cx={cx}
        cy={cy}
        r={selected ? 6 : 4}
        fill={selected ? color : hollow ? "#0b0e14" : "#ffffff"}
        stroke={color}
        strokeWidth={2}
      />
    </g>
  );
}

function soldCarTitle(car) {
  const title = [car?.year, car?.make, car?.model, car?.trim]
    .filter((part) => part != null && String(part).trim())
    .join(" ");
  return title || "Sold vehicle";
}

function soldPrice(value) {
  if (value == null || value === "") return "";
  const number = Number(value);
  if (!Number.isFinite(number)) return "";
  return `$${number.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

function MonthListPager({ total, page, pageSize, onPage, onPageSize, testPrefix }) {
  if (!total) return null;
  const { size, totalPages, safePage, start } = monthPageWindow(total, page, pageSize);
  const shown = Math.min(size, total - start);
  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-3">
      <p className="text-xs font-['Manrope'] text-white/45">
        Showing {shown} of {total}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={size}
          onChange={(event) => onPageSize(Number(event.target.value))}
          aria-label="Rows per page"
          className="min-h-11 bg-white/5 border border-white/10 text-white px-3 font-['Manrope'] focus:outline-none focus:border-blue-500/50 appearance-none cursor-pointer [&>option]:bg-gray-900 [&>option]:text-white"
          data-testid={`${testPrefix}-page-size`}
        >
          {MONTH_PAGE_SIZES.map((option) => (
            <option key={option} value={option}>
              {option} per page
            </option>
          ))}
        </select>
        {totalPages > 1 && (
          <>
            <button
              type="button"
              disabled={safePage <= 1}
              onClick={() => onPage(safePage - 1)}
              className="min-h-11 px-3 border border-white/10 text-sm font-['Manrope'] text-white/70 disabled:opacity-40"
              data-testid={`${testPrefix}-page-prev`}
            >
              Previous
            </button>
            <span className="text-xs font-['Manrope'] text-white/45">
              Page {safePage} of {totalPages}
            </span>
            <button
              type="button"
              disabled={safePage >= totalPages}
              onClick={() => onPage(safePage + 1)}
              className="min-h-11 px-3 border border-white/10 text-sm font-['Manrope'] text-white/70 disabled:opacity-40"
              data-testid={`${testPrefix}-page-next`}
            >
              Next
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function SoldMonthCars({ month, canOpen, onOpen, page, pageSize, onPage, onPageSize }) {
  const cars = Array.isArray(month?.vehicles) ? month.vehicles : null;
  const count = Number(month?.count) || 0;
  const pageWindow = monthPageWindow(cars?.length || 0, page, pageSize);
  const visibleCars = Array.isArray(cars) ? cars.slice(pageWindow.start, pageWindow.end) : [];
  let body;
  if (cars == null || (cars.length === 0 && count > 0)) {
    body = (
      <p className="text-sm font-['Manrope'] text-white/50">
        Sold cars for this month could not be loaded.
      </p>
    );
  } else if (cars.length === 0) {
    body = (
      <p className="text-sm font-['Manrope'] text-white/50">No vehicles sold in {month.label}.</p>
    );
  } else {
    body = (
      <ul className="divide-y divide-white/10" data-testid="sold-month-cars">
        {visibleCars.map((car, index) => {
          const price = soldPrice(car.sale_price);
          const meta = [
            car.stock_number ? `Stock ${car.stock_number}` : "",
            car.sold_at ? `Sold ${formatDateMdY(car.sold_at, "—")}` : "",
          ].filter(Boolean).join(" · ");
          const content = (
            <>
              {car.image ? (
                <img src={car.image} alt="" className="w-16 h-12 object-cover shrink-0" />
              ) : (
                <span className="w-16 h-12 shrink-0 bg-white/5" aria-hidden="true" />
              )}
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-['Manrope'] font-semibold text-white truncate">
                  {soldCarTitle(car)}
                </span>
                {meta && (
                  <span className="block text-xs font-['Manrope'] text-white/45 truncate">{meta}</span>
                )}
                {car.vin && (
                  <span className="block text-xs font-['Manrope'] text-white/35 truncate">{car.vin}</span>
                )}
              </span>
              {price && (
                <span className="shrink-0 text-sm font-['Manrope'] text-white/80">{price}</span>
              )}
            </>
          );
          return (
            <li key={car.id || `${car.vin || "car"}-${index}`}>
              {canOpen && car.id ? (
                <button
                  type="button"
                  onClick={() => onOpen(car)}
                  className="w-full min-h-11 flex items-center gap-3 px-1 py-2 text-left hover:bg-white/5"
                >
                  {content}
                </button>
              ) : (
                <div className="w-full min-h-11 flex items-center gap-3 px-1 py-2">{content}</div>
              )}
            </li>
          );
        })}
      </ul>
    );
  }
  return (
    <div className="admin-glass-flat p-3 md:p-4" data-testid="sold-month-card">
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <h3 className="font-['Unbounded'] text-sm text-white">{month.label}</h3>
        <p className="text-xs font-['Manrope'] uppercase tracking-wide text-white/45">{count} sold</p>
      </div>
      {body}
      {Array.isArray(cars) && cars.length > 0 && (
        <MonthListPager
          total={cars.length}
          page={page}
          pageSize={pageSize}
          onPage={onPage}
          onPageSize={onPageSize}
          testPrefix="sold-month"
        />
      )}
    </div>
  );
}

function SoldSplineTooltip({ active, payload, label, tipBg, tipFg, unit = "sold" }) {
  if (!active || !payload?.length) return null;
  const count = Number(payload[0]?.value) || 0;
  return (
    <div
      style={{
        background: tipBg,
        border: "1px solid var(--admin-border)",
        borderRadius: 10,
        color: tipFg,
        padding: "8px 12px",
      }}
    >
      <p style={{ margin: 0, fontFamily: "Manrope, sans-serif", fontSize: 12, opacity: 0.72 }}>{label}</p>
      <p style={{ margin: "2px 0 0", fontFamily: "Manrope, sans-serif", fontSize: 14, fontWeight: 600 }}>
        {count} {unit}
      </p>
    </div>
  );
}

function LeadMonthList({ month, canOpen, onOpen, page, pageSize, onPage, onPageSize }) {
  const leads = Array.isArray(month?.leads) ? month.leads : null;
  const count = Number(month?.count) || 0;
  const pageWindow = monthPageWindow(leads?.length || 0, page, pageSize);
  const visibleLeads = Array.isArray(leads) ? leads.slice(pageWindow.start, pageWindow.end) : [];
  let body;
  if (leads == null || (leads.length === 0 && count > 0)) {
    body = (
      <p className="text-sm font-['Manrope'] text-white/50">Leads for this month could not be loaded.</p>
    );
  } else if (leads.length === 0) {
    body = (
      <p className="text-sm font-['Manrope'] text-white/50">No leads in {month.label}.</p>
    );
  } else {
    body = (
      <ul className="divide-y divide-white/10" data-testid="lead-month-list">
        {visibleLeads.map((lead, index) => {
          const when = lead.created_at ? formatDateTimeMdY(lead.created_at, "—") : "";
          const meta = [lead.type ? getLeadTypeLabel(lead.type) : "", when].filter(Boolean).join(" · ");
          const content = (
            <>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-['Manrope'] font-semibold text-white truncate">
                  {lead.name || "Lead"}
                </span>
                {meta && (
                  <span className="block text-xs font-['Manrope'] text-white/45 truncate">{meta}</span>
                )}
              </span>
            </>
          );
          return (
            <li key={`${lead.type || "lead"}-${lead.id || index}`}>
              {canOpen && lead.id ? (
                <button
                  type="button"
                  onClick={() => onOpen(lead)}
                  className="w-full min-h-11 flex items-center gap-3 px-1 py-2 text-left hover:bg-white/5"
                >
                  {content}
                </button>
              ) : (
                <div className="w-full min-h-11 flex items-center gap-3 px-1 py-2">{content}</div>
              )}
            </li>
          );
        })}
      </ul>
    );
  }
  return (
    <div className="admin-glass-flat p-3 md:p-4" data-testid="lead-month-card">
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <h3 className="font-['Unbounded'] text-sm text-white">{month.label}</h3>
        <p className="text-xs font-['Manrope'] uppercase tracking-wide text-white/45">{count} leads</p>
      </div>
      {body}
      {Array.isArray(leads) && leads.length > 0 && (
        <MonthListPager
          total={leads.length}
          page={page}
          pageSize={pageSize}
          onPage={onPage}
          onPageSize={onPageSize}
          testPrefix="lead-month"
        />
      )}
    </div>
  );
}

function MonthSpline({
  months,
  color,
  fillId,
  glowId,
  unit,
  listLabel,
  dotPrefix,
  interactive = true,
  expandable = true,
  selectedKey,
  panelOpen,
  onSelect,
  isDark,
  reduceMotion,
  tick,
  grid,
  tipBg,
  tipFg,
}) {
  const renderDot = (persistent) => (dotProps) => (
    <SoldMonthDot
      {...dotProps}
      selectedKey={selectedKey}
      panelOpen={panelOpen}
      onSelect={onSelect}
      hollow={isDark}
      color={color}
      unit={unit}
      listLabel={listLabel}
      testPrefix={dotPrefix}
      persistent={persistent}
      interactive={interactive}
      expandable={expandable}
    />
  );
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={months} margin={{ top: 16, right: 12, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={isDark ? 0.34 : 0.2} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
          <filter id={glowId} x="-20%" y="-40%" width="140%" height="180%">
            <feDropShadow dx="0" dy="0" stdDeviation="2" floodColor={color} floodOpacity={isDark ? 0.5 : 0.25} />
          </filter>
        </defs>
        <CartesianGrid stroke={grid} vertical={false} />
        <XAxis dataKey="label" tick={{ fill: tick, fontSize: 11 }} interval={0} angle={-35} textAnchor="end" height={56} />
        <YAxis allowDecimals={false} domain={[0, "dataMax + 1"]} tick={{ fill: tick, fontSize: 11 }} width={32} />
        <Tooltip
          cursor={{ stroke: isDark ? "rgba(255,255,255,0.22)" : "rgba(15,23,42,0.18)", strokeWidth: 1, pointerEvents: "none" }}
          content={<SoldSplineTooltip tipBg={tipBg} tipFg={tipFg} unit={unit} />}
          wrapperStyle={{ pointerEvents: "none" }}
          isAnimationActive={!reduceMotion}
        />
        <Area
          type="monotone"
          dataKey="count"
          stroke="none"
          fill={`url(#${fillId})`}
          activeDot={false}
          isAnimationActive={!reduceMotion}
        />
        <Line
          type="monotone"
          dataKey="count"
          name={unit}
          stroke={color}
          strokeWidth={2.75}
          strokeLinecap="round"
          strokeLinejoin="round"
          dot={renderDot(true)}
          activeDot={renderDot(false)}
          filter={`url(#${glowId})`}
          isAnimationActive={!reduceMotion}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

function VisitorLeadTooltip({ active, payload, label, tipBg, tipFg }) {
  if (!active || !payload?.length) return null;
  const month = payload[0]?.payload || {};
  const visitors = Number(month.visitors) || 0;
  const leads = Number(month.count) || 0;
  return (
    <div
      style={{
        background: tipBg,
        border: "1px solid var(--admin-border)",
        borderRadius: 10,
        color: tipFg,
        padding: "8px 12px",
      }}
    >
      <p style={{ margin: 0, fontFamily: "Manrope, sans-serif", fontSize: 12, opacity: 0.72 }}>{label}</p>
      <p style={{ margin: "2px 0 0", fontFamily: "Manrope, sans-serif", fontSize: 14, fontWeight: 600 }}>
        {visitors} visitors
      </p>
      <p style={{ margin: "2px 0 0", fontFamily: "Manrope, sans-serif", fontSize: 14, fontWeight: 600 }}>
        {leads} leads
      </p>
    </div>
  );
}

function VisitorLeadSpline({
  months,
  visitorColor,
  leadColor,
  selectedKey,
  panelOpen,
  onSelect,
  isDark,
  reduceMotion,
  tick,
  grid,
  tipBg,
  tipFg,
  interactive,
}) {
  const peak = months.reduce(
    (max, month) => Math.max(max, Number(month?.visitors) || 0, Number(month?.count) || 0),
    0
  );
  const visitorDot = (persistent) => (dotProps) => (
    <SoldMonthDot
      {...dotProps}
      selectedKey={selectedKey}
      panelOpen={panelOpen}
      onSelect={onSelect}
      hollow={isDark}
      color={visitorColor}
      unit="visitors"
      listLabel="visitors"
      testPrefix="visitor-vs-visitor-dot"
      persistent={persistent}
      interactive={interactive}
      expandable={false}
    />
  );
  const leadDot = (persistent) => (dotProps) => (
    <SoldMonthDot
      {...dotProps}
      selectedKey={selectedKey}
      panelOpen={panelOpen}
      onSelect={onSelect}
      hollow={isDark}
      color={leadColor}
      unit="leads"
      listLabel="leads"
      testPrefix="visitor-vs-lead-dot"
      persistent={persistent}
      interactive={interactive}
      expandable
    />
  );
  return (
    <div className="h-full flex flex-col">
      <div className="flex flex-wrap gap-4 pb-2">
        <span className="inline-flex items-center gap-2 text-xs font-['Manrope'] text-white/70">
          <span className="inline-block w-4 h-1" style={{ background: visitorColor }} aria-hidden="true" />
          Visitors
        </span>
        <span className="inline-flex items-center gap-2 text-xs font-['Manrope'] text-white/70">
          <span className="inline-block w-4 h-1" style={{ background: leadColor }} aria-hidden="true" />
          Leads
        </span>
      </div>
      <div className="min-h-0 flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={months} margin={{ top: 16, right: 12, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="visitorVsFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={visitorColor} stopOpacity={isDark ? 0.28 : 0.16} />
                <stop offset="100%" stopColor={visitorColor} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="leadVsFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={leadColor} stopOpacity={isDark ? 0.28 : 0.16} />
                <stop offset="100%" stopColor={leadColor} stopOpacity={0} />
              </linearGradient>
              <filter id="visitorVsGlow" x="-20%" y="-40%" width="140%" height="180%">
                <feDropShadow dx="0" dy="0" stdDeviation="2" floodColor={visitorColor} floodOpacity={isDark ? 0.45 : 0.22} />
              </filter>
              <filter id="leadVsGlow" x="-20%" y="-40%" width="140%" height="180%">
                <feDropShadow dx="0" dy="0" stdDeviation="2" floodColor={leadColor} floodOpacity={isDark ? 0.45 : 0.22} />
              </filter>
            </defs>
            <CartesianGrid stroke={grid} vertical={false} />
            <XAxis dataKey="label" tick={{ fill: tick, fontSize: 11 }} interval={0} angle={-35} textAnchor="end" height={56} />
            <YAxis allowDecimals={false} domain={[0, peak + 1]} tick={{ fill: tick, fontSize: 11 }} width={40} />
            <Tooltip
              cursor={{ stroke: isDark ? "rgba(255,255,255,0.22)" : "rgba(15,23,42,0.18)", strokeWidth: 1, pointerEvents: "none" }}
              content={<VisitorLeadTooltip tipBg={tipBg} tipFg={tipFg} />}
              wrapperStyle={{ pointerEvents: "none" }}
              isAnimationActive={!reduceMotion}
            />
            <Area type="monotone" dataKey="visitors" stroke="none" fill="url(#visitorVsFill)" activeDot={false} isAnimationActive={!reduceMotion} />
            <Area type="monotone" dataKey="count" stroke="none" fill="url(#leadVsFill)" activeDot={false} isAnimationActive={!reduceMotion} />
            <Line
              type="monotone"
              dataKey="visitors"
              name="Visitors"
              stroke={visitorColor}
              strokeWidth={2.75}
              strokeLinecap="round"
              strokeLinejoin="round"
              dot={visitorDot(true)}
              activeDot={visitorDot(false)}
              filter="url(#visitorVsGlow)"
              isAnimationActive={!reduceMotion}
            />
            <Line
              type="monotone"
              dataKey="count"
              name="Leads"
              stroke={leadColor}
              strokeWidth={2.75}
              strokeLinecap="round"
              strokeLinejoin="round"
              dot={leadDot(true)}
              activeDot={leadDot(false)}
              filter="url(#leadVsGlow)"
              isAnimationActive={!reduceMotion}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function errorText(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail.trim();
  if (Array.isArray(detail)) {
    const text = detail
      .map((item) => (typeof item === "string" ? item : item?.msg))
      .filter(Boolean)
      .join(" ");
    if (text) return text;
  }
  return "Could not load the dashboard.";
}

function MissedList({ items, empty, onOpen, timeKey }) {
  if (!items.length) {
    return <p className="admin-faint text-sm font-['Manrope']">{empty}</p>;
  }
  return (
    <ul className="admin-glass-flat divide-y divide-white/10">
      {items.map((item) => (
        <li key={`${item.type || "item"}-${item.id}`}>
          <button
            type="button"
            onClick={() => onOpen(item)}
            className="w-full text-left px-3 py-2.5 hover:bg-white/5"
          >
            <span className="block text-sm font-['Manrope'] text-white truncate">{item.name}</span>
            <span className="block text-xs font-['Manrope'] text-white/40 truncate">
              {item.type ? `${getLeadTypeLabel(item.type)} · ` : ""}
              {item.preview || formatDateTimeMdY(item[timeKey] || item.created_at || item.at, "")}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function notificationLine(item) {
  return [item.kind_label, item.detail, formatDateTimeMdY(item.created_at, "")]
    .filter(Boolean)
    .join(" · ");
}

function NotificationList({ items, onOpen, empty }) {
  if (!items.length) {
    return <p className="admin-faint text-sm font-['Manrope']">{empty}</p>;
  }
  return (
    <ul className="admin-glass-flat divide-y divide-white/10">
      {items.map((item) => (
        <li key={item.id || item.created_at}>
          <button
            type="button"
            onClick={() => onOpen(item)}
            className="relative w-full text-left px-3 py-2.5 hover:bg-white/5"
            data-testid={`notification-${item.id}`}
          >
            {(item.employee_pending || item.missed_since_login) && (
              <span className="absolute left-3 top-2">
                <NewSinceLoginPill />
              </span>
            )}
            <span className={`block text-sm font-['Manrope'] text-white truncate ${item.employee_pending || item.missed_since_login ? "pt-3.5" : ""}`}>
              {item.title || "Notification"}
            </span>
            <span className="block text-xs font-['Manrope'] text-white/40 truncate">
              {notificationLine(item)}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function NotificationsDialog({
  open,
  token,
  page,
  pageSize,
  data,
  error,
  loading,
  onClose,
  onPage,
  onPageSize,
  onOpen,
}) {
  useAttentionSeen(open ? "notifications" : "", token);
  const total = Number(data?.total) || 0;
  return (
    <AdminDialog open={open} onClose={onClose} title="Notifications" testId="notifications-dialog">
      {loading && <p className="text-sm font-['Manrope'] text-white/50">Loading notifications…</p>}
      {!loading && error && <p className="text-sm font-['Manrope'] text-red-300">{error}</p>}
      {!loading && !error && (
        <>
          <NotificationList
            items={data?.items || []}
            empty="No notifications yet."
            onOpen={onOpen}
          />
          <MonthListPager
            total={total}
            page={page}
            pageSize={pageSize}
            onPage={onPage}
            onPageSize={onPageSize}
            testPrefix="notifications"
          />
        </>
      )}
    </AdminDialog>
  );
}

function InsightCard({ title, count, action, onAction, children, testId }) {
  return (
    <section className="admin-glass admin-enter p-4 md:p-5" data-testid={testId}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="font-['Unbounded'] text-base text-white">{title}</h2>
          {count != null && (
            <p className="text-2xl font-['Unbounded'] text-white mt-2">{count}</p>
          )}
        </div>
        {action && (
          <button
            type="button"
            onClick={onAction}
            className="shrink-0 min-h-11 px-3 text-xs font-['Manrope'] font-semibold uppercase tracking-wide bg-blue-500 hover:bg-blue-600 text-white"
          >
            {action}
          </button>
        )}
      </div>
      {children}
    </section>
  );
}

export default function AdminInsightsPage() {
  const navigate = useNavigate();
  const { token, hasSection } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [chartIndex, setChartIndex] = useState(0);
  const [selectedKey, setSelectedKey] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [monthPageSize, setMonthPageSize] = useState(DEFAULT_MONTH_PAGE_SIZE);
  const [monthPage, setMonthPage] = useState(1);
  const [noteOpen, setNoteOpen] = useState(false);
  const [notePage, setNotePage] = useState(1);
  const [notePageSize, setNotePageSize] = useState(DEFAULT_MONTH_PAGE_SIZE);
  const [noteData, setNoteData] = useState(null);
  const [noteError, setNoteError] = useState("");
  const [noteLoading, setNoteLoading] = useState(false);
  const openFrame = useRef(0);

  const load = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/dashboard/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSummary(res.data || {});
    } catch (err) {
      setSummary(null);
      setError(errorText(err));
    } finally {
      setLoading(false);
    }
  }, [token]);

  const refreshSummary = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/dashboard/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSummary(res.data || {});
    } catch (err) {
      // Keep the cards already on screen when a quiet refresh fails.
    }
  }, [token]);

  const loadNotes = useCallback(async (page, pageSize) => {
    if (!token) return;
    setNoteLoading(true);
    setNoteError("");
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, page_size: pageSize },
      });
      const body = res.data || {};
      setNoteData(body);
      if (body.page) setNotePage(body.page);
    } catch (err) {
      setNoteData(null);
      setNoteError("Notifications could not be loaded.");
    } finally {
      setNoteLoading(false);
    }
  }, [token]);

  const openNotes = () => {
    setNotePage(1);
    setNoteOpen(true);
    loadNotes(1, notePageSize);
  };

  const closeNotes = async () => {
    setNoteOpen(false);
    if (token) {
      try {
        await axios.post(
          `${BACKEND_URL}/api/admin/attention/seen`,
          { surface: "notifications" },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (err) {
        // Leaving the dialog also sends this with keepalive.
      }
    }
    refreshSummary();
  };

  const openNotification = async (item, acknowledge) => {
    if (!item?.href) return;
    if (item.employee_pending && item.id && token) {
      try {
        await axios.post(
          `${BACKEND_URL}/api/admin/notifications/${item.id}/opened`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (err) {
        // The chat still opens. The pill stays if this ack did not save.
      }
    }
    if (acknowledge) await closeNotes();
    navigate(item.href);
  };

  const changeNotePageSize = (size) => {
    const next = MONTH_PAGE_SIZES.includes(size) ? size : DEFAULT_MONTH_PAGE_SIZE;
    setNotePageSize(next);
    setNotePage(1);
    loadNotes(1, next);
  };

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => () => cancelAnimationFrame(openFrame.current), []);

  const soldMonths = summary?.sold_by_month || [];
  const soldTotal = soldMonths.reduce((sum, month) => sum + (month.count || 0), 0);
  const soldFailed = Boolean(summary?.errors?.includes("sold"));
  const canOpenSold = hasSection("inventory");
  const showLeadChart = hasSection("leads");
  const leadMonths = Array.isArray(summary?.leads_by_month) ? summary.leads_by_month : [];
  const leadFailed = showLeadChart && (
    Boolean(summary?.errors?.includes("lead_months")) || !Array.isArray(summary?.leads_by_month)
  );
  const leadTotal = leadMonths.reduce((sum, month) => sum + (month.count || 0), 0);
  const canOpenLeads = hasSection("leads");
  const notePreview = Array.isArray(summary?.notifications?.items) ? summary.notifications.items : [];
  const notesFailed = Boolean(summary?.errors?.includes("notifications"));
  const noteMissed = Number(summary?.notifications?.missed_count) || 0;
  const noteHiddenRaw = Number(summary?.notifications?.hidden_new_count) || 0;
  const noteHidden = noteHiddenRaw > 0 ? noteHiddenRaw : Math.max(0, noteMissed - 5);
  const visitorMonths = Array.isArray(summary?.visitors_by_month) ? summary.visitors_by_month : [];
  const visitorFailed = Boolean(summary?.errors?.includes("visitors")) || !Array.isArray(summary?.visitors_by_month);
  const visitorTotal = visitorMonths.reduce((sum, month) => sum + (month.count || 0), 0);
  const reduceMotion = useReducedMotion();
  const { isDark } = useTheme();
  const tick = isDark ? "rgba(248,250,252,0.62)" : "rgba(15,23,42,0.62)";
  const grid = isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)";
  const tipBg = isDark ? "rgba(11,14,20,0.96)" : "rgba(255,255,255,0.96)";
  const tipFg = isDark ? "#f8fafc" : "#0f172a";
  const leadColor = isDark ? "#34d399" : "#059669";
  const charts = [
    {
      id: "sold",
      title: "Vehicles sold",
      totalLabel: `${soldTotal} sold in the last 12 months`,
      failed: soldFailed,
      error: "Sold totals could not be loaded.",
      months: soldMonths,
      color: "#3b82f6",
      fillId: "soldSplineFill",
      glowId: "soldSplineGlow",
      unit: "sold",
      listLabel: "cars",
      dotPrefix: "sold-month-dot",
      expand: true,
      action: canOpenSold ? "View all sold vehicles" : "",
      actionTestId: "view-all-sold-vehicles",
      onAction: () => navigate("/admin/sold-vehicles"),
    },
  ];
  if (showLeadChart) {
    charts.push({
      id: "leads",
      title: "Leads",
      totalLabel: `${leadTotal} leads in the last 12 months`,
      failed: leadFailed,
      error: "Lead totals could not be loaded.",
      months: leadMonths,
      color: leadColor,
      fillId: "leadSplineFill",
      glowId: "leadSplineGlow",
      unit: "leads",
      listLabel: "leads",
      dotPrefix: "lead-month-dot",
      expand: true,
      action: canOpenLeads ? "View all leads" : "",
      actionTestId: "view-all-leads",
      onAction: () => navigate("/admin/leads"),
    });
  }
  charts.push({
    id: "visitors",
    title: "Visitors",
    totalLabel: `${visitorTotal} visitors in the last 12 months`,
    failed: visitorFailed,
    error: "Visitor totals could not be loaded.",
    months: visitorMonths,
    color: "#f59e0b",
    fillId: "visitorSplineFill",
    glowId: "visitorSplineGlow",
    unit: "visitors",
    listLabel: "visitors",
    dotPrefix: "visitor-month-dot",
    expand: false,
    action: "",
  });
  if (showLeadChart) {
    const comparisonFailed = visitorFailed || leadFailed;
    let comparisonError = "Visitors and leads could not be loaded.";
    if (visitorFailed && !leadFailed) comparisonError = "Visitor totals could not be loaded.";
    if (!visitorFailed && leadFailed) comparisonError = "Lead totals could not be loaded.";
    charts.push({
      id: "visitors-vs-leads",
      title: "Visitors VS Leads",
      totalLabel: `${visitorTotal} visitors and ${leadTotal} leads in the last 12 months`,
      failed: comparisonFailed,
      error: comparisonError,
      months: comparisonFailed ? [] : visitorLeadSeries(visitorMonths, leadMonths),
      compare: true,
      expand: true,
      action: canOpenLeads ? "View all leads" : "",
      actionTestId: "view-all-visitor-leads",
      onAction: () => navigate("/admin/leads"),
    });
  }
  const activeIndex = charts.length ? chartIndex % charts.length : 0;
  const active = charts[activeIndex];
  const selectedMonth = active.failed
    ? null
    : active.months.find((month) => month.key === selectedKey) || null;

  const chooseMonth = (month) => {
    if (active.expand === false) return;
    const key = month?.key;
    if (!key) return;
    cancelAnimationFrame(openFrame.current);
    if (key === selectedKey && panelOpen) {
      setPanelOpen(false);
      return;
    }
    if (key !== selectedKey) setMonthPage(1);
    const wasOpen = panelOpen && selectedKey;
    setSelectedKey(key);
    if (wasOpen || reduceMotion) {
      setPanelOpen(true);
      return;
    }
    setPanelOpen(false);
    openFrame.current = requestAnimationFrame(() => {
      openFrame.current = requestAnimationFrame(() => setPanelOpen(true));
    });
  };

  const stepChart = (direction) => {
    if (charts.length < 2) return;
    cancelAnimationFrame(openFrame.current);
    setPanelOpen(false);
    setSelectedKey(null);
    setMonthPage(1);
    setChartIndex((current) => (current + direction + charts.length) % charts.length);
  };

  const changeMonthPageSize = (size) => {
    setMonthPageSize(MONTH_PAGE_SIZES.includes(size) ? size : DEFAULT_MONTH_PAGE_SIZE);
    setMonthPage(1);
  };

  return (
    <div className="p-4 sm:p-6 max-w-[1400px] mx-auto" data-testid="admin-insights">
      <AdminPageHeader
        title="Dashboard"
        subtitle="What changed since the last time you signed in"
      />
      {loading && <p className="text-sm font-['Manrope'] text-white/50">Loading insights…</p>}
      {error && (
        <div className="mb-4">
          <p className="text-sm font-['Manrope'] text-red-300">{error}</p>
          <button
            type="button"
            onClick={load}
            className="mt-3 min-h-11 px-3 text-xs font-['Manrope'] font-semibold uppercase tracking-wide bg-white/10 text-white"
          >
            Try again
          </button>
        </div>
      )}
      {!loading && !error && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <section
            className="admin-glass admin-enter p-4 md:p-5 xl:col-span-2"
            data-testid="insight-graphs"
            data-active-chart={active.id}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0" aria-live="polite">
                <h2 className="font-['Unbounded'] text-base text-white">{active.title}</h2>
                {!active.failed && (
                  <p className="text-sm font-['Manrope'] text-white/45 mt-1">{active.totalLabel}</p>
                )}
              </div>
              {active.action && (
                <button
                  type="button"
                  onClick={active.onAction}
                  className="shrink-0 min-h-11 px-3 text-xs font-['Manrope'] font-semibold uppercase tracking-wide bg-blue-500 hover:bg-blue-600 text-white"
                  data-testid={active.actionTestId}
                >
                  {active.action}
                </button>
              )}
            </div>
            <div className={`relative mt-4${charts.length > 1 ? " pb-14" : ""}`}>
              <div className="overflow-hidden">
                <div
                  className="admin-graph-track flex w-full"
                  style={{ transform: `translate3d(${-activeIndex * 100}%, 0, 0)` }}
                >
                  {charts.map((chart) => {
                    const isActive = chart.id === active.id;
                    return (
                    <div
                      key={chart.id}
                      className={`w-full min-w-full shrink-0${isActive ? "" : " pointer-events-none"}`}
                      aria-hidden={isActive ? undefined : true}
                    >
                      {chart.failed ? (
                        <p className="h-64 flex items-center text-sm font-['Manrope'] text-red-300">
                          {chart.error}
                        </p>
                      ) : chart.compare ? (
                        <div className="h-64">
                          <VisitorLeadSpline
                            months={chart.months}
                            visitorColor="#f59e0b"
                            leadColor={leadColor}
                            selectedKey={isActive ? selectedKey : null}
                            panelOpen={isActive && panelOpen}
                            onSelect={chooseMonth}
                            isDark={isDark}
                            reduceMotion={reduceMotion}
                            tick={tick}
                            grid={grid}
                            tipBg={tipBg}
                            tipFg={tipFg}
                            interactive={isActive}
                          />
                        </div>
                      ) : (
                        <div className="h-64">
                          <MonthSpline
                            months={chart.months}
                            color={chart.color}
                            fillId={chart.fillId}
                            glowId={chart.glowId}
                            unit={chart.unit}
                            listLabel={chart.listLabel}
                            dotPrefix={chart.dotPrefix}
                            interactive={isActive}
                            expandable={chart.expand !== false}
                            selectedKey={isActive ? selectedKey : null}
                            panelOpen={isActive && panelOpen}
                            onSelect={chooseMonth}
                            isDark={isDark}
                            reduceMotion={reduceMotion}
                            tick={tick}
                            grid={grid}
                            tipBg={tipBg}
                            tipFg={tipFg}
                          />
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>
              {charts.length > 1 && (
                <div className="absolute bottom-0 right-0 z-10 flex gap-2">
                  <button
                    type="button"
                    onClick={() => stepChart(-1)}
                    className="inline-flex items-center justify-center min-h-11 min-w-11 bg-white/10 hover:bg-white/15 border border-white/15 text-white"
                    aria-label="Previous graph"
                    data-testid="insight-graph-prev"
                  >
                    <ChevronLeft className="w-5 h-5" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={() => stepChart(1)}
                    className="inline-flex items-center justify-center min-h-11 min-w-11 bg-white/10 hover:bg-white/15 border border-white/15 text-white"
                    aria-label="Next graph"
                    data-testid="insight-graph-next"
                  >
                    <ChevronRight className="w-5 h-5" aria-hidden="true" />
                  </button>
                </div>
              )}
            </div>
            {active.expand !== false && selectedMonth && (
              <div
                className={`admin-sold-month${panelOpen ? " is-open" : ""}`}
                data-testid="sold-month-panel"
                aria-hidden={panelOpen ? undefined : true}
                {...(panelOpen ? {} : { inert: "" })}
              >
                <div className="admin-sold-month-inner">
                  <div className="pt-4">
                    {active.id === "leads" || active.id === "visitors-vs-leads" ? (
                      <LeadMonthList
                        month={selectedMonth}
                        canOpen={canOpenLeads}
                        onOpen={(lead) => navigate(`/admin/leads/${lead.id}`)}
                        page={monthPage}
                        pageSize={monthPageSize}
                        onPage={setMonthPage}
                        onPageSize={changeMonthPageSize}
                      />
                    ) : (
                      <SoldMonthCars
                        month={selectedMonth}
                        canOpen={canOpenSold}
                        onOpen={(car) => navigate(`/admin/sold-vehicles/${car.id}`)}
                        page={monthPage}
                        pageSize={monthPageSize}
                        onPage={setMonthPage}
                        onPageSize={changeMonthPageSize}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          </section>

          <section
            className="admin-glass admin-enter p-4 md:p-5 xl:col-span-2"
            data-testid="notifications-card"
          >
            <div className="flex items-start justify-between gap-3">
              <h2 className="font-['Unbounded'] text-base text-white">Notifications</h2>
              <div className="flex items-center gap-2 shrink-0">
                {noteMissed > 5 && (
                  <span className="inline-flex items-center gap-1" data-testid="notifications-more-new">
                    <NewSinceLoginPill />
                    <span className="text-[8px] leading-none font-['Manrope'] font-semibold text-white">{noteHidden}</span>
                  </span>
                )}
                <button
                  type="button"
                  onClick={openNotes}
                  className="min-h-11 px-3 text-xs font-['Manrope'] font-semibold uppercase tracking-wide bg-blue-500 hover:bg-blue-600 text-white"
                  data-testid="view-all-notifications"
                >
                  View all notifications
                </button>
              </div>
            </div>
            <div className="mt-4 min-h-64">
              {notesFailed ? (
                <p className="text-sm font-['Manrope'] text-red-300">Notifications could not be loaded.</p>
              ) : (
                <NotificationList
                  items={notePreview}
                  empty="No notifications yet."
                  onOpen={(item) => openNotification(item, false)}
                />
              )}
            </div>
          </section>

          {summary?.errors?.includes("leads") && (
            <p className="text-sm font-['Manrope'] text-red-300">New leads could not be loaded.</p>
          )}
          {summary?.leads && (
            <InsightCard
              title="New leads"
              count={summary.leads.count}
              action="View New Leads"
              onAction={() => navigate("/admin/leads")}
              testId="missed-leads-card"
            >
              <MissedList
                items={summary.leads.items || []}
                empty="No new leads since your last login."
                timeKey="created_at"
                onOpen={(item) => navigate(`/admin/leads/${item.id}`)}
              />
            </InsightCard>
          )}

          {summary?.errors?.includes("sms") && (
            <p className="text-sm font-['Manrope'] text-red-300">SMS replies could not be loaded.</p>
          )}
          {summary?.sms && (
            <InsightCard
              title="SMS replies"
              count={summary.sms.count}
              action="View SMS"
              onAction={() => navigate("/admin/outreach?tab=sms")}
              testId="missed-sms-card"
            >
              <MissedList
                items={(summary.sms.items || []).map((item) => ({ ...item, preview: item.preview }))}
                empty="No SMS replies since your last login."
                timeKey="at"
                onOpen={(item) => navigate(`/admin/outreach?tab=sms&thread=${item.id}`)}
              />
            </InsightCard>
          )}

          {summary?.errors?.includes("whatsapp") && (
            <p className="text-sm font-['Manrope'] text-red-300">WhatsApp replies could not be loaded.</p>
          )}
          {summary?.whatsapp && (
            <InsightCard
              title="WhatsApp replies"
              count={summary.whatsapp.count}
              action="View WhatsApp"
              onAction={() => navigate("/admin/outreach?tab=whatsapp")}
              testId="missed-whatsapp-card"
            >
              <MissedList
                items={summary.whatsapp.items || []}
                empty="No WhatsApp replies since your last login."
                timeKey="at"
                onOpen={(item) => navigate(`/admin/outreach?tab=whatsapp&thread=${item.id}`)}
              />
            </InsightCard>
          )}
        </div>
      )}
      <NotificationsDialog
        open={noteOpen}
        token={token}
        page={notePage}
        pageSize={notePageSize}
        data={noteData}
        error={noteError}
        loading={noteLoading}
        onClose={closeNotes}
        onPage={(page) => {
          setNotePage(page);
          loadNotes(page, notePageSize);
        }}
        onPageSize={changeNotePageSize}
        onOpen={(item) => openNotification(item, true)}
      />
    </div>
  );
}
